/**
 * useRegisterBusiness ViewModel Hook
 * Extracts all state management and business logic from RegisterBusiness page.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCategories as fetchCategoriesUseCase } from '../domain/useCases/Business/fetchCategories.js';
import { registerBusiness } from '../domain/useCases/Business/registerBusiness.js';
import { checkTagAvailability as checkTag } from '../domain/useCases/Business/checkTagAvailability.js';
import { fetchProfile } from '../domain/useCases/User/fetchProfile.js';
import { fetchCurrentUser } from '../domain/useCases/User/fetchCurrentUser.js';
import { reverseGeocode } from '../domain/useCases/Location/reverseGeocode.js';
import { searchLocation } from '../domain/useCases/Location/searchLocation.js';
import { extractNepalLocationFromAddress } from '../utils/nepalLocationMapping';
import { fixImageUrlsInObject } from '../utils/urlFixer.js';
import { storageService } from '../domain/services/StorageService.js';
import { fetchMainCategoryFormSkeleton } from '../domain/useCases/Business/fetchFormSkeleton.js';
import { useUpload } from './useUpload.js';

const ENTITY_LABELS = {
    'Business': { name: 'Business Name', hint: 'business', tag: 'KAHA TAG' },
    'Sampada': { name: 'Sampada Name', hint: 'sampada', tag: 'KAHA TAG' },
    'Organization': { name: 'Organization Name', hint: 'organization', tag: 'KAHA TAG' },
    'Tourist place': { name: 'Tourist Place Name', hint: 'tourist place', tag: 'KAHA TAG' },
    'Home': { name: 'Home Name', hint: 'home', tag: 'KAHA TAG' },
};

const PREFIX_MAP = {
    'Business': 'b',
    'Sampada': 's',
    'Organization': 'o',
    'Tourist place': 'r',
    'Home': 'h',
};

const INITIAL_FORM_DATA = {
    categoryId: '',
    entityType: '',
    grouped: '',
    tag: '',
    tagLine: '',
    contactNumber: '',
    secondaryContact: '',
    email: '',
    website: '',
    latitude: '',
    longitude: '',
    mapAddress: {
        street: '',
        district: '',
        province: '',
        municipality: '',
        wardNo: '',
        generalAddress: '',
        additionalInfo: '',
    },
    locatedInBusinessId: '',
    buildingInformation: '',
    floorNo: '',
    avatar: '',
    coverImageUrl: '',
    buildingImageUrl: '',
    gallery: [],
    description: '',
    workingDaysAndHours: {},
};

export const useRegisterBusiness = (userData) => {
    const navigate = useNavigate();
    const { uploadSingleFile, uploadMultipleFiles, uploading: uploadingImage } = useUpload();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [mainCategories, setMainCategories] = useState([]);
    const [selectedMainCategory, setSelectedMainCategory] = useState('');
    const [selectedMainCategoryObj, setSelectedMainCategoryObj] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSubcategory, setSelectedSubcategory] = useState('');
    const [showLocationPicker, setShowLocationPicker] = useState(false);
    const [showHoursModal, setShowHoursModal] = useState(false);
    const [newGalleryUrl, setNewGalleryUrl] = useState('');
    // Kathmandu coordinates as default
    const [mapCenter, setMapCenter] = useState([27.7172, 85.3240]);
    const [userContact, setUserContact] = useState('');
    const [tagError, setTagError] = useState('');
    const [tagSuccess, setTagSuccess] = useState('');
    const [checkingTag, setCheckingTag] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [formSkeleton, setFormSkeleton] = useState(null);
    const [loadingFormSkeleton, setLoadingFormSkeleton] = useState(false);

    const avatarInputRef = useRef(null);
    const coverInputRef = useRef(null);
    const buildingInputRef = useRef(null);
    const galleryInputRef = useRef(null);
    const fetchInitializedRef = useRef(false);
    const searchTimeoutRef = useRef(null);
    const dynamicImageRefs = useRef({ current: {} });
    const uploadInProgressRef = useRef(false);

    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [mainCategoryId, setMainCategoryId] = useState('');

    const totalSteps = 6;

    const getEntityLabel = () => ENTITY_LABELS[selectedMainCategory] || ENTITY_LABELS['Business'];

    // ── Load Form Skeleton ─────────────────────────────────────
    useEffect(() => {
        // Only load form skeleton if a category has been selected (not just main category)
        if (selectedMainCategory && selectedCategory) {
            loadFormSkeleton();
        }
    }, [selectedMainCategory, selectedCategory]);

    const loadFormSkeleton = async () => {
        if (!selectedMainCategory) return;
        
        console.log('Loading form skeleton for:', selectedMainCategory);
        setLoadingFormSkeleton(true);
        try {
            const skeleton = await fetchMainCategoryFormSkeleton(selectedMainCategory, 'registration');
            console.log('Form skeleton loaded:', skeleton);
            setFormSkeleton(skeleton);
        } catch (err) {
            console.error('Error loading form skeleton:', err);
            setFormSkeleton(null);
        } finally {
            setLoadingFormSkeleton(false);
        }
    };

    // ── Init ───────────────────────────────────────────────────
    useEffect(() => {
        const token = storageService.getAuthToken();
        if (!token) { navigate('/login'); return; }
        if (fetchInitializedRef.current) return;
        fetchInitializedRef.current = true;

        if (userData) {
            const contact = userData.contactNumber || userData.landline || userData.contact || '';
            setUserContact(contact);
            setFormData(prev => ({ ...prev, contactNumber: contact }));
        } else {
            fetchUserData();
        }
        loadCategories();
    }, []);

    const fetchUserData = async () => {
        try {
            let data;
            try { data = await fetchProfile(); }
            catch { try { data = await fetchCurrentUser(); } catch { data = null; } }
            if (data) {
                const contact = data.contactNumber || data.landline || data.contact || '';
                setUserContact(contact);
                setFormData(prev => ({ ...prev, contactNumber: contact }));
            }
        } catch (err) {
            console.error('Error fetching user data:', err);
        }
    };

    const loadCategories = async () => {
        setCategoriesLoading(true);
        try {
            // Clear old cache to force fresh fetch
            localStorage.removeItem('kaha_categories_cache');
            localStorage.removeItem('kaha_categories_cache_time');

            // Fetch from API
            const data = await fetchCategoriesUseCase();
            console.log('Fresh categories from API:', data);
            let list = [];
            if (Array.isArray(data)) list = data;
            else if (data.data && Array.isArray(data.data)) list = data.data;
            else if (data.items && Array.isArray(data.items)) list = data.items;
            
            console.log('Processed categories list:', list);
            
            const filteredList = list.filter(cat => cat.name !== 'SOS');
            setMainCategories(filteredList);
            
            // Cache the categories
            const now = Date.now();
            localStorage.setItem('kaha_categories_cache', JSON.stringify(filteredList));
            localStorage.setItem('kaha_categories_cache_time', now.toString());
        } catch (err) {
            console.error('Error fetching categories:', err);
            setError('Failed to load categories');
        } finally {
            setCategoriesLoading(false);
        }
    };

    // ── Category helpers ───────────────────────────────────────
    const getSubcategoriesForMain = useCallback((mainCategoryName) => {
        const mainCat = mainCategories.find(cat => cat.name === mainCategoryName);
        return mainCat?.childrens || [];
    }, [mainCategories]);

    const getSubcategoriesForCategory = useCallback((categoryId) => {
        for (const mainCat of mainCategories) {
            if (mainCat.childrens) {
                const cat = mainCat.childrens.find(c => c.id === categoryId);
                if (cat?.childrens) return cat.childrens;
            }
        }
        return [];
    }, [mainCategories]);

    // Build category lookup map for fast O(1) lookups
    const categoryLookupMap = useMemo(() => {
        const map = {};
        mainCategories.forEach(mc => {
            if (mc.childrens) {
                mc.childrens.forEach(cat => {
                    if (cat.childrens) {
                        cat.childrens.forEach(sub => {
                            map[sub.id] = mc.name;
                        });
                    }
                });
            }
        });
        return map;
    }, [mainCategories]);

    // ── Form handlers ──────────────────────────────────────────
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let finalValue = type === 'checkbox' ? checked : value;
        if (name === 'tag') {
            finalValue = value.toUpperCase();
            setTagError('');
            setTagSuccess('');
        }
        setFormData(prev => ({ ...prev, [name]: finalValue }));
    };

    const handleMapAddressChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            mapAddress: { ...prev.mapAddress, [name]: value },
        }));
    };

    // ── Tag checking ───────────────────────────────────────────
    const checkTagAvailability = async (tag) => {
        if (!tag.trim()) { setTagError(''); setTagSuccess(''); return; }
        setCheckingTag(true);
        try {
            const data = await checkTag(tag);
            console.log('Tag availability response:', data);
            
            // If isAvailableTag is true, the tag is NOT available (already taken)
            if (data.isAvailableTag === true) {
                setTagError(`The KahaTag @${tag} is not available.`);
                setTagSuccess('');
            } else {
                setTagError('');
                setTagSuccess(`Congratulations! The TAG @${tag} is available`);
            }
        } catch (err) {
            console.error('Error checking tag:', err);
            setTagError('Could not verify tag availability');
            setTagSuccess('');
        } finally {
            setCheckingTag(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            if (formData.tag.trim()) checkTagAvailability(formData.tag);
        }, 500);
        return () => clearTimeout(timer);
    }, [formData.tag]);

    // ── Gallery handlers ───────────────────────────────────────
    const handleAddGalleryImage = () => {
        if (newGalleryUrl.trim() && formData.gallery.length < 5) {
            setFormData(prev => ({ ...prev, gallery: [...prev.gallery, newGalleryUrl.trim()] }));
            setNewGalleryUrl('');
        }
    };

    const handleRemoveGalleryImage = (index) => {
        setFormData(prev => ({ ...prev, gallery: prev.gallery.filter((_, i) => i !== index) }));
    };

    // ── Hours handlers ─────────────────────────────────────────
    const handleHoursChange = (day, field, value) => {
        setFormData(prev => ({
            ...prev,
            workingDaysAndHours: {
                ...prev.workingDaysAndHours,
                [day]: {
                    ...(typeof prev.workingDaysAndHours[day] === 'object' ? prev.workingDaysAndHours[day] : {}),
                    [field]: value,
                },
            },
        }));
    };

    const applyHoursToAllDays = (openTime, closeTime) => {
        if (openTime && closeTime) {
            ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].forEach(day => {
                handleHoursChange(day, 'open', openTime);
                handleHoursChange(day, 'close', closeTime);
            });
        }
    };

    // ── Image upload ───────────────────────────────────────────
    const handleImageUpload = async (e, fieldName) => {
        const file = e.target.files?.[0];
        if (!file) {
            console.warn('No file selected for', fieldName);
            return;
        }

        console.log('handleImageUpload: Starting upload for', fieldName, 'file:', file.name);
        uploadInProgressRef.current = true;
        try {
            const uploadedUrl = await uploadSingleFile(file);
            console.log('handleImageUpload: Upload successful for', fieldName, 'URL:', uploadedUrl);
            console.log('handleImageUpload: URL type:', typeof uploadedUrl);
            console.log('handleImageUpload: URL is valid:', uploadedUrl && typeof uploadedUrl === 'string' && uploadedUrl.length > 0);
            
            // Verify URL before setting
            if (!uploadedUrl || typeof uploadedUrl !== 'string' || uploadedUrl.length === 0) {
                throw new Error(`Invalid URL returned for ${fieldName}: ${uploadedUrl}`);
            }
            
            setFormData(prev => {
                const updated = { ...prev, [fieldName]: uploadedUrl };
                console.log('handleImageUpload: Updated formData[' + fieldName + ']:', updated[fieldName]);
                console.log('handleImageUpload: Verify formData update - current value:', updated[fieldName]);
                console.log('handleImageUpload: Verify formData update - type:', typeof updated[fieldName]);
                return updated;
            });
        } catch (err) {
            console.error('Upload error:', err);
            setError(err.message || 'Failed to upload image');
        } finally {
            uploadInProgressRef.current = false;
        }
    };

    const handleGalleryUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) {
            console.warn('No files selected for gallery');
            return;
        }

        console.log('handleGalleryUpload: Starting upload for', files.length, 'files');
        console.log('handleGalleryUpload: Current gallery length:', formData.gallery.length);
        const remaining = 5 - formData.gallery.length;
        const filesToProcess = files.slice(0, remaining);
        console.log('handleGalleryUpload: Files to process:', filesToProcess.length, 'Remaining slots:', remaining);

        uploadInProgressRef.current = true;
        try {
            // Upload each file one at a time using single file upload
            const uploadedUrls = [];
            for (const file of filesToProcess) {
                console.log('handleGalleryUpload: Uploading file:', file.name);
                const url = await uploadSingleFile(file);
                console.log('handleGalleryUpload: Received URL from uploadSingleFile:', url);
                console.log('handleGalleryUpload: URL type:', typeof url);
                console.log('handleGalleryUpload: URL is valid:', url && typeof url === 'string' && url.length > 0);
                
                if (!url || typeof url !== 'string' || url.length === 0) {
                    throw new Error(`Invalid URL returned for gallery file: ${url}`);
                }
                
                uploadedUrls.push(url);
                console.log('handleGalleryUpload: File uploaded successfully:', url);
            }
            
            console.log('handleGalleryUpload: All uploads successful, URLs:', uploadedUrls);
            console.log('handleGalleryUpload: URLs array length:', uploadedUrls.length);
            setFormData(prev => {
                const updated = {
                    ...prev,
                    gallery: [...prev.gallery, ...uploadedUrls]
                };
                console.log('handleGalleryUpload: Updated gallery:', updated.gallery);
                console.log('handleGalleryUpload: Updated gallery length:', updated.gallery.length);
                return updated;
            });
        } catch (err) {
            console.error('Gallery upload error:', err);
            setError(err.message || 'Failed to upload gallery images');
        } finally {
            uploadInProgressRef.current = false;
            // Add small delay to ensure React state is flushed
            await new Promise(resolve => setTimeout(resolve, 100));
        }
    };

    // ── Location ───────────────────────────────────────────────
    // Helper function to find location codes from names
    const findLocationCodes = async (provinceNameFromAPI, districtNameFromAPI, municipalityNameFromAPI) => {
        try {
            const response = await fetch('/json/location.json');
            if (!response.ok) return { province: '', district: '', municipality: '' };
            
            const data = await response.json();
            const locationData = data.item;
            
            let provinceCode = '';
            let districtCode = '';
            let municipalityCode = '';
            
            // Find province by name (case-insensitive partial match)
            if (provinceNameFromAPI) {
                const province = locationData.provinces?.find(p => 
                    p.label.toLowerCase().includes(provinceNameFromAPI.toLowerCase()) ||
                    p.alt_name.toLowerCase().includes(provinceNameFromAPI.toLowerCase()) ||
                    provinceNameFromAPI.toLowerCase().includes(p.label.toLowerCase())
                );
                if (province) {
                    provinceCode = province.code;
                    
                    // Find district within this province
                    if (districtNameFromAPI) {
                        const district = province.districts?.find(d =>
                            d.label.toLowerCase().includes(districtNameFromAPI.toLowerCase()) ||
                            d.alt_name.toLowerCase().includes(districtNameFromAPI.toLowerCase()) ||
                            districtNameFromAPI.toLowerCase().includes(d.label.toLowerCase())
                        );
                        if (district) {
                            districtCode = district.code;
                            
                            // Find municipality within this district
                            if (municipalityNameFromAPI) {
                                const municipality = district.municipalities?.find(m =>
                                    m.label.toLowerCase().includes(municipalityNameFromAPI.toLowerCase()) ||
                                    m.alt_name.toLowerCase().includes(municipalityNameFromAPI.toLowerCase()) ||
                                    municipalityNameFromAPI.toLowerCase().includes(m.label.toLowerCase())
                                );
                                if (municipality) {
                                    municipalityCode = municipality.code;
                                }
                            }
                        }
                    }
                }
            }
            
            return { province: provinceCode, district: districtCode, municipality: municipalityCode };
        } catch (err) {
            console.error('Error finding location codes:', err);
            return { province: '', district: '', municipality: '' };
        }
    };

    const getLocationFromCoordinates = async (lat, lon, preserveAddress = false) => {
        try {
            const result = await reverseGeocode(lat, lon);
            if (result) {
                const address = result.display_name || '';
                
                // Extract location names from Nominatim response
                const locationInfo = extractNepalLocationFromAddress(result.address || {});
                
                // Convert location names to codes
                const locationCodes = await findLocationCodes(
                    locationInfo.province,
                    locationInfo.district,
                    locationInfo.municipality
                );

                console.log('Location data from reverse geocode:', {
                    address,
                    locationInfo,
                    locationCodes,
                    preserveAddress
                });

                setMapCenter([lat, lon]);
                
                setFormData(prev => ({
                    ...prev,
                    latitude: lat.toFixed(8),
                    longitude: lon.toFixed(8),
                    mapAddress: {
                        ...prev.mapAddress,
                        generalAddress: preserveAddress ? prev.mapAddress.generalAddress : address,
                        street: result.address?.road || result.address?.neighbourhood || '',
                        province: locationCodes.province || prev.mapAddress.province,
                        district: locationCodes.district || prev.mapAddress.district,
                        municipality: locationCodes.municipality || prev.mapAddress.municipality,
                    },
                }));
                
                return { success: true };
            }
        } catch (err) { 
            console.error('Reverse geocode error:', err);
            return { success: false, error: err };
        }
    };

    const handleSearchLocation = (query) => {
        setSearchQuery(query);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        if (query.trim().length > 2) {
            searchTimeoutRef.current = setTimeout(async () => {
                try {
                    console.log('Searching for:', query);
                    const results = await searchLocation(query);
                    console.log('Search results:', results);
                    
                    if (results && results.length > 0) {
                        const result = results[0];
                        console.log('First result:', result);
                        
                        // Handle both lat/lon and latitude/longitude formats
                        const lat = parseFloat(result.lat || result.latitude);
                        const lng = parseFloat(result.lon || result.longitude);
                        
                        console.log('Extracted coordinates:', lat, lng);
                        
                        if (!isNaN(lat) && !isNaN(lng)) {
                            // Update map center to pan to the search result
                            setMapCenter([lat, lng]);
                            console.log('Map center updated to:', [lat, lng]);
                            
                            // Get location details from coordinates
                            await getLocationFromCoordinates(lat, lng);
                        } else {
                            console.error('Invalid coordinates from search result');
                        }
                    } else {
                        console.log('No search results found');
                    }
                } catch (err) { 
                    console.error('Search error:', err);
                }
            }, 800);
        }
    };

    // ── Step navigation ────────────────────────────────────────
    const handleNextStep = () => {
        if (step === 1) {
            // For Home, set default business type
            if (selectedMainCategory === 'Home') {
                const defaultType = selectedMainCategoryObj?.businessTypes?.[0]?.id;
                if (defaultType) setFormData(prev => ({ ...prev, categoryId: defaultType }));
                setError(null);
                setStep(2);
                window.scrollTo(0, 0);
                return;
            }
            
            // For other entity types with businessTypes (Sampada, Tourist Place, Organization)
            if (selectedMainCategory && selectedMainCategoryObj?.businessTypes?.length > 0) {
                // If businessTypes exist, require selection
                if (!selectedCategory) { setError('Please select a type'); return; }
                setFormData(prev => ({ ...prev, categoryId: selectedCategory }));
                setError(null);
                setStep(2);
                window.scrollTo(0, 0);
                return;
            }
            
            // For Business and other types without businessTypes
            const finalCategoryId = selectedSubcategory || selectedCategory;
            if (!finalCategoryId) { setError('Please select a category or type'); return; }
            setFormData(prev => ({ ...prev, categoryId: finalCategoryId }));
            setError(null);
            setStep(2);
        } else if (step === 2) {
            if (!formData.grouped.trim()) { setError('Please enter business name'); return; }
            if (!formData.tag.trim()) { setError('Please enter KAHA TAG'); return; }
            if (tagError) { setError('Please use an available KAHA TAG'); return; }
            setError(null);
            setStep(3);
        } else if (step === 3) {
            if (!formData.mapAddress.generalAddress && !formData.mapAddress.wardNo) { setError('Please select a location'); return; }
            setError(null);
            setStep(4);
        } else if (step === 4) {
            if (!formData.avatar) { setError('Please upload a business avatar'); return; }
            if (!formData.coverImageUrl) { setError('Please upload a cover image'); return; }
            setError(null);
            setStep(5);
        }
        window.scrollTo(0, 0);
    };

    const handlePreviousStep = () => {
        if (step > 1) { setError(null); setStep(step - 1); }
        window.scrollTo(0, 0);
    };

    // ── Submit ─────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Check if any upload is in progress
        if (uploadInProgressRef.current) {
            setError('Please wait for image uploads to complete');
            return;
        }
        
        setLoading(true);
        setError(null);

        try {
            const categoryIdToSubmit = selectedSubcategory || selectedCategory || formData.categoryId;
            if (!categoryIdToSubmit) { setError('Please select at least one category'); setLoading(false); return; }

            // Check if avatar is empty - warn but allow submission
            if (!formData.avatar || formData.avatar.trim() === '') {
                console.warn('Avatar is empty - backend will use default category icon');
                // Don't block submission, just warn
            } else if (!formData.avatar.startsWith('http')) {
                // Validate avatar URL format only if avatar exists
                setError('Avatar URL is invalid. Please re-upload the avatar.');
                setLoading(false);
                return;
            }

            // Use selectedMainCategory for the entity prefix (more reliable than lookup map)
            let mainCategoryName = selectedMainCategory || 'Business';

            // Convert working hours from object format to string format (9:0-17:0 format)
            const workingHoursPayload = {};
            Object.entries(formData.workingDaysAndHours || {}).forEach(([day, hours]) => {
                if (hours?.open === 'closed' || hours?.close === 'closed') {
                    workingHoursPayload[day] = 'closed';
                } else if (hours?.open && hours?.close) {
                    // Convert to simple format: 9:0-17:0 (not 09:00-17:00)
                    const openParts = hours.open.split(':');
                    const closeParts = hours.close.split(':');
                    workingHoursPayload[day] = `${parseInt(openParts[0])}:${parseInt(openParts[1])}-${parseInt(closeParts[0])}:${parseInt(closeParts[1])}`;
                }
            });

            // Parse latitude and longitude as NUMBERS (backend expects numbers)
            const lat = formData.latitude ? parseFloat(formData.latitude) : null;
            const lng = formData.longitude ? parseFloat(formData.longitude) : null;

            const payload = {
                tag: formData.tag,
                name: formData.grouped,
                mapAddress: {
                    street: formData.mapAddress.street || '',
                    country: 'Nepal',
                    generalAddress: formData.mapAddress.generalAddress || '',
                    latitude: lat,
                    longitude: lng,
                    province: formData.mapAddress.province ? String(formData.mapAddress.province) : '',
                    district: formData.mapAddress.district ? String(formData.mapAddress.district) : '',
                    municipality: formData.mapAddress.municipality ? String(formData.mapAddress.municipality) : '',
                    wardNo: formData.mapAddress.wardNo ? String(formData.mapAddress.wardNo) : '',
                    additionalInfo: formData.mapAddress.additionalInfo || null,
                },
                address: formData.mapAddress.generalAddress || '',
                latitude: lat,  // NUMBER (not string)
                longitude: lng, // NUMBER (not string)
                isPickup: false,  // BOOLEAN (not string)
                isDelivery: false, // BOOLEAN (not string)
                workingDaysAndHours: workingHoursPayload,
                avatarUrl: formData.avatar,
                coverImageUrl: formData.coverImageUrl,
                categoryId: categoryIdToSubmit,
                entityPrefix: PREFIX_MAP[mainCategoryName] || 'b',
            };

            console.log('handleSubmit: Avatar value being sent:', formData.avatar);
            console.log('handleSubmit: Avatar type:', typeof formData.avatar);
            console.log('handleSubmit: Avatar is valid:', formData.avatar && typeof formData.avatar === 'string' && formData.avatar.length > 0);
            console.log('handleSubmit: Avatar starts with http:', formData.avatar.startsWith('http'));
            console.log('handleSubmit: CoverImageUrl value being sent:', formData.coverImageUrl);
            console.log('handleSubmit: BuildingImageUrl value being sent:', formData.buildingImageUrl);

            // Add businessTypeIds if this is a Sampada, Tourist Place, or Organization
            if (selectedMainCategory !== 'Home' && selectedMainCategory !== 'Business') {
                payload.businessTypeIds = [categoryIdToSubmit];
                // For these entity types, entityType should be the selected businessType ID
                payload.entityType = categoryIdToSubmit;
                // Keep categoryId - the backend requires it
                // categoryId should be the main category ID for these entity types
                let finalCategoryId = mainCategoryId;
                if (!finalCategoryId && selectedMainCategoryObj?.id) {
                    finalCategoryId = selectedMainCategoryObj.id;
                }
                if (finalCategoryId) {
                    payload.categoryId = finalCategoryId;
                }
                console.log('Non-Business Entity - Using categoryId:', finalCategoryId, 'businessTypeIds:', [categoryIdToSubmit]);
            }

            // Add optional fields if they exist
            if (formData.buildingImageUrl) payload.buildingImageUrl = formData.buildingImageUrl;
            if (formData.gallery && formData.gallery.length > 0) payload.gallery = formData.gallery.slice(0, 5);
            if (formData.description) payload.description = formData.description;
            if (formData.tagLine) payload.tagLine = formData.tagLine;
            if (formData.contactNumber) payload.contact = formData.contactNumber;
            if (formData.secondaryContact) payload.secondaryContact = formData.secondaryContact;
            if (formData.email) payload.email = formData.email;
            if (formData.website) payload.website = formData.website;
            if (formData.buildingInformation) payload.buildingInformation = formData.buildingInformation;
            if (formData.floorNo) payload.floorNo = formData.floorNo;

            console.log('Register Business Payload:', JSON.stringify(payload, null, 2));
            console.log('Payload keys:', Object.keys(payload));
            console.log('Payload types:', Object.entries(payload).map(([k, v]) => `${k}: ${typeof v}`));
            
            // Remove null values from payload
            Object.keys(payload).forEach(key => {
                if (payload[key] === null || payload[key] === undefined) {
                    delete payload[key];
                }
            });
            
            // Also clean up mapAddress
            if (payload.mapAddress) {
                Object.keys(payload.mapAddress).forEach(key => {
                    if (payload.mapAddress[key] === null || payload.mapAddress[key] === undefined) {
                        delete payload.mapAddress[key];
                    }
                });
            }
            
            console.log('Cleaned Payload:', JSON.stringify(payload, null, 2));
            console.log('Entity Type:', mainCategoryName);
            console.log('Category ID:', categoryIdToSubmit);
            console.log('Main Category ID:', mainCategoryId);
            
            await registerBusiness(payload);
            setSuccess('Business registered successfully!');
            setTimeout(() => navigate('/dashboard?tab=my-businesses'), 1500);
        } catch (err) {
            console.error('Registration error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return {
        // State
        currentStep: step, totalSteps, loading, categoriesLoading, error, success,
        mainCategories, selectedMainCategory, selectedMainCategoryObj,
        selectedCategory, selectedSubcategory,
        showLocationPicker, showHoursModal,
        formData, newGalleryUrl, mapCenter, uploadingImage,
        userContact, tagError, tagSuccess, checkingTag, searchQuery,
        formSkeleton, loadingFormSkeleton,
        // Refs
        avatarInputRef, coverInputRef, buildingInputRef, galleryInputRef, dynamicImageRefs, uploadInProgressRef,
        // Computed
        getEntityLabel, getSubcategoriesForMain, getSubcategoriesForCategory,
        // Handlers
        handleChange, handleMapAddressChange,
        handleAddGalleryImage, handleRemoveGalleryImage,
        handleHoursChange, applyHoursToAllDays,
        handleImageUpload, handleGalleryUpload,
        getLocationFromCoordinates, handleSearchLocation,
        nextStep: handleNextStep, prevStep: handlePreviousStep, handleSubmit,
        // Upload functions
        uploadSingleFile, uploadMultipleFiles,
        // Setters
        setFormData, setSelectedMainCategory, setSelectedMainCategoryObj,
        setSelectedCategory, setSelectedSubcategory,
        setShowLocationPicker, setShowHoursModal,
        setNewGalleryUrl, setSearchQuery, setError,
        setMainCategoryId,
        navigate,
    };
};
